import React from 'react';
import { Todo, TodoNavigationSummary } from '../types/data';
import TodoItem from './TodoItem';
import Button from './Button';
import MenuButton from './MenuButton';
import Icon from './icons/Icon';
import { isShoppingHabitId } from '../utils/habits';
import { useTags } from '../hooks/useTags';
import { useHabits } from '../hooks/useHabits';
import './TodoList.css';
import { isTodoExpired } from '../data/todoData';
import { addTokyoDays, getTokyoDateParts, getTodayTokyoDateString, parseTokyoDateParts } from '../utils/dateUtils';

type ViewMode = 'date' | 'tag' | 'habit';

interface TodoListProps {
  todos: Todo[];
  selectedDate: string | null;
  viewMode?: ViewMode;
  selectedTagId?: string | null;
  selectedHabitId?: string | null;
  navigationSummary?: TodoNavigationSummary | null;
  onToggleTodo: (todoId: string) => void;
  onShowTodoDetail?: (todoId: string) => void;
  onDelete?: (todoId: string) => void;
  selectedTodoId?: string | null;
  onCreateNewTodo?: () => void;
  onEditTag?: () => void;
  onDeleteTag?: (tagId: string) => void;
  onEditHabit?: () => void;
  onDeleteHabit?: (habitId: string) => void;
  isDrawerOpen?: boolean;
}

const TodoList: React.FC<TodoListProps> = ({
  todos,
  selectedDate,
  viewMode = 'date',
  selectedTagId,
  selectedHabitId,
  navigationSummary,
  onToggleTodo,
  onShowTodoDetail,
  onDelete,
  selectedTodoId,
  onCreateNewTodo,
  onEditTag,
  onDeleteTag,
  onEditHabit,
  onDeleteHabit,
  isDrawerOpen = false
}) => {
  const { getTagById } = useTags();
  const { getHabitById } = useHabits();
  const tagCountById = React.useMemo(() => {
    const map = new Map<string | null, number>();
    navigationSummary?.tag_counts.forEach(item => map.set(item.tag_id, item.open_count));
    return map;
  }, [navigationSummary]);
  const habitCountById = React.useMemo(() => {
    const map = new Map<string, number>();
    navigationSummary?.habit_counts.forEach(item => map.set(item.habit_id, item.open_count));
    return map;
  }, [navigationSummary]);

  const getOpenTodoCount = () => todos.filter(t => !t.done && !t.canceled).length;
  const formatDate = (dateString: string): string => {
    const date = parseTokyoDateParts(dateString) ?? getTokyoDateParts(dateString);
    const normalized = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
    const today = getTodayTokyoDateString();
    if (normalized === today) {
      return '今日';
    } else if (normalized === addTokyoDays(today, -1)) {
      return '昨日';
    } else if (normalized === addTokyoDays(today, 1)) {
      return '明日';
    }
    const weekday = ['日', '月', '火', '水', '木', '金', '土'][new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay()];
    return `${date.month}月${date.day}日(${weekday})`;
  };

  // 期限切れ判定は共通ヘルパーに統一

  // 月を取得（YYYY年M月形式）
  const getMonthLabel = (dateString: string): string => {
    const { year, month } = getTokyoDateParts(dateString);
    return `${year}年${month}月`;
  };

  // 月別にTodoをグループ化（タグ・習慣別表示用）
  const groupByMonth = (todos: Todo[]) => {
    const groups: { [key: string]: Todo[] } = {};
    todos.forEach(todo => {
      const monthKey = getMonthLabel(todo.startAt);
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(todo);
    });

    // 月の降順でソート
    const sortedMonths = Object.keys(groups).sort((a, b) => {
      const dateA = new Date(groups[a][0].startAt);
      const dateB = new Date(groups[b][0].startAt);
      return dateB.getTime() - dateA.getTime();
    });

    return sortedMonths.map(month => ({
      month,
      todos: groups[month]
    }));
  };

  // ソート順: 時間が早いものから順に表示（日付別表示用）
  const sortByStartTimeAsc = (todos: Todo[]) => {
    return [...todos].sort((a, b) => {
      const timeA = new Date(a.startAt).getTime();
      const timeB = new Date(b.startAt).getTime();
      return timeA - timeB; // 昇順（早い時間から）
    });
  };

  // ソート順: 時間が新しいものから順に表示（タグ・習慣別表示用）
  const sortByStartTimeDesc = (todos: Todo[]) => {
    return [...todos].sort((a, b) => {
      const timeA = new Date(a.startAt).getTime();
      const timeB = new Date(b.startAt).getTime();
      return timeB - timeA; // 降順（新しい時間から）
    });
  };

  // タイトルとアイコンを取得
  const getHeaderInfo = () => {
    if (viewMode === 'tag') {
      // 「未分類」は selectedTagId を null として扱う（ルーティング都合）
      if (selectedTagId === null || selectedTagId === 'uncategorized') {
        return {
          icon: <Icon name="empty" size={24} />,
          title: '未分類',
          count: tagCountById.get(null) ?? getOpenTodoCount()
        };
      } else if (selectedTagId) {
        const tag = getTagById(selectedTagId);
        return {
          icon: <Icon name="tag" size={24} />,
          title: tag?.title || 'タグ',
          count: tagCountById.get(selectedTagId) ?? getOpenTodoCount()
        };
      }
    } else if (viewMode === 'habit') {
      if (selectedHabitId) {
        const habit = getHabitById(selectedHabitId);
        const iconName = isShoppingHabitId(selectedHabitId) ? 'shop' : 'habit';
        return {
          icon: <Icon name={iconName} size={24} />,
          title: habit?.title || '習慣',
          count: habitCountById.get(selectedHabitId) ?? getOpenTodoCount()
        };
      }
    }

    // 日付別表示（デフォルト）
    return {
      icon: null,
      title: selectedDate ? formatDate(selectedDate) : 'TODO リスト',
      count: getOpenTodoCount()
    };
  };

  const headerInfo = getHeaderInfo();

  // ビューモードに応じてTodoを整理
  let todoSections: Array<{ title: string; todos: Todo[] }> = [];

  if (viewMode === 'date') {
    // 日付別表示: 未完了/完了済みセクション
    const completedTodos = sortByStartTimeAsc(todos.filter(todo => todo.done));
    const pendingTodos = sortByStartTimeAsc(todos.filter(todo => !todo.done));

    if (pendingTodos.length > 0) {
      todoSections.push({ title: '未完了', todos: pendingTodos });
    }
    if (completedTodos.length > 0) {
      todoSections.push({ title: '完了済み', todos: completedTodos });
    }
  } else {
    // タグ別・習慣別表示: 月別セクション
    const sortedTodos = sortByStartTimeDesc(todos);
    const monthGroups = groupByMonth(sortedTodos);

    todoSections = monthGroups.map(group => ({
      title: group.month,
      todos: group.todos
    }));
  }

  return (
    <div className="todo-list">
      <div className="todo-list-header">
        <div className="todo-list-header-content">
          <div className="todo-list-header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {headerInfo.icon}
              <h2 className="text-heading-large">{headerInfo.title}</h2>
            </div>
            <div className="todo-count text-body-small">
              {headerInfo.count}件の未対応タスク
            </div>
          </div>
          <div className="todo-list-header-right">
            <Button leftIcon="add" onClick={onCreateNewTodo}>新規追加</Button>
            {viewMode === 'tag' && selectedTagId !== null && selectedTagId !== 'uncategorized' && (
              <MenuButton
                icon="menu"
                aria-label="タグ操作メニュー"
                tooltip="タグ操作メニュー"
                tooltipPosition="bottom-left"
                items={[
                  {
                    type: 'Default',
                    label: 'タグの編集',
                    leftIcon: 'edit',
                    showLeftIcon: true,
                    onClick: () => {
                      onEditTag?.();
                    }
                  },
                  {
                    type: 'Destructive',
                    label: 'タグの削除',
                    leftIcon: 'delete',
                    showLeftIcon: true,
                    onClick: () => {
                      if (selectedTagId && selectedTagId !== 'uncategorized') {
                        onDeleteTag?.(selectedTagId);
                      }
                    }
                  }
                ]}
              />
            )}
            {viewMode === 'habit' && selectedHabitId && (
              <MenuButton
                icon="menu"
                aria-label="習慣操作メニュー"
                tooltip="習慣操作メニュー"
                tooltipPosition="bottom-left"
                items={[
                  {
                    type: 'Default',
                    label: '習慣の編集',
                    leftIcon: 'edit',
                    showLeftIcon: true,
                    onClick: () => {
                      onEditHabit?.();
                    }
                  },
                  ...(!isShoppingHabitId(selectedHabitId) ? [{
                    type: 'Destructive' as const,
                    label: '習慣の削除',
                    leftIcon: 'delete' as const,
                    showLeftIcon: true,
                    onClick: () => {
                      onDeleteHabit?.(selectedHabitId);
                    }
                  }] : [])
                ]}
              />
            )}
          </div>
        </div>
      </div>

      {todos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h3 className="text-heading-medium">TODOがありません</h3>
          <p className="text-body-small">
            {viewMode === 'tag' && 'このタグのTODOはまだ登録されていません。'}
            {viewMode === 'habit' && 'この習慣のTODOはまだ登録されていません。'}
            {viewMode === 'date' && 'この日にはまだTODOが登録されていません。'}
          </p>
        </div>
      ) : (
        <div className="todo-sections">
          {todoSections.map((section, index) => (
            <div key={index} className="todo-section">
              <h3 className="section-title text-heading-medium">{section.title}</h3>
              <div className="todo-items">
                {section.todos.map(todo => (
                  <TodoItem
                    key={todo.id}
                    id={todo.id}
                    title={(() => {
                      if (todo.habitId) {
                        const h = getHabitById(todo.habitId);
                        return h?.title || todo.title;
                      }
                      return todo.title;
                    })()}
                    body={todo.body}
                    startAt={todo.startAt}
                    done={todo.done}
                    isExpired={isTodoExpired(todo)}
                    doneAt={todo.doneAt}
                    habitId={todo.habitId}
                    onItemClick={() => onShowTodoDetail?.(todo.id)}
                    onToggleComplete={onToggleTodo}
                    onDelete={onDelete}
                    isSelected={selectedTodoId === todo.id}
                    disabled={isDrawerOpen}
                    showDateInTime={viewMode !== 'date'}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TodoList;
